// Replica of the gym admin dashboard (sidebar, top bar, dashboard page) filled with demo data.
// It is laid out at real screen size and scaled to fit, so it reads like a screenshot, and it
// cycles through demo gyms to show white-labelling: colour, logo, domain, gym and theme change.
// Layout and labels mirror layouts/DashboardLayout, components/sidebar, components/header and
// app/(dashboard)/dashboard — keep them roughly in step when the admin changes.
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import type { CSSProperties, ReactNode } from "react"
import {
  ActivityIcon,
  ArrowUpRight,
  BarChart3,
  Building2,
  Calendar,
  ChevronDown,
  ChevronRight,
  CreditCard,
  CreditCardIcon,
  Dumbbell,
  DumbbellIcon,
  LayoutDashboard,
  Menu,
  MessageSquare,
  Moon,
  Mountain,
  QrCode,
  Receipt,
  ScanLine,
  Search,
  ShoppingBag,
  Sparkles,
  Star,
  Sun,
  Tag,
  UserCog,
  Users,
  UsersIcon,
  Utensils,
  Volume2,
  Waves,
  Zap,
} from "lucide-react"
import { BrowserChrome, FloatingChip, usePrefersReducedMotion } from "./visuals"

// ─── Demo gyms ───────────────────────────────────────────────────────────────

type DemoTenant = {
  name: string
  host: string
  color: string
  dark: boolean
  location: string
  Logo: typeof Dumbbell
  stats: { total: number; totalChange: number; active: number; activeChange: number; revenue: number; revenueChange: number; inGym: number }
  /** Check-ins per hour from 06:00 to 22:00. */
  hours: number[]
  members: string[]
}

export const DEMO_TENANTS: DemoTenant[] = [
  {
    name: "Iron House", host: "ironhouse", color: "#E11D48", dark: false, location: "Tirana Center", Logo: Dumbbell,
    stats: { total: 1284, totalChange: 12, active: 963, activeChange: 8, revenue: 3852000, revenueChange: 15, inGym: 47 },
    hours: [6, 18, 31, 24, 14, 10, 12, 16, 13, 11, 19, 33, 44, 38, 26, 15, 6],
    members: ["Andi Bardhi", "Klara Hoxha", "Era Krasniqi", "Bledi Shehu", "Sara Leka", "Dritan Gjoka", "Ana Prifti"],
  },
  {
    name: "Pulse Studio", host: "pulse", color: "#7C3AED", dark: false, location: "Blloku", Logo: Zap,
    stats: { total: 642, totalChange: 18, active: 511, activeChange: 11, revenue: 2044000, revenueChange: 9, inGym: 23 },
    hours: [2, 9, 22, 28, 17, 8, 5, 9, 14, 10, 12, 24, 31, 27, 19, 9, 3],
    members: ["Megi Kola", "Ardit Rama", "Jona Muça", "Elton Duka", "Kevin Basha", "Ina Topalli", "Lori Hasa"],
  },
  {
    name: "Peak CrossFit", host: "peak", color: "#F59E0B", dark: true, location: "Durrës", Logo: Mountain,
    stats: { total: 318, totalChange: 22, active: 287, activeChange: 14, revenue: 1435000, revenueChange: 19, inGym: 31 },
    hours: [10, 24, 19, 9, 6, 4, 6, 8, 7, 9, 16, 29, 34, 25, 12, 5, 2],
    members: ["Gent Cela", "Arta Zeneli", "Besi Lika", "Noel Marku", "Dea Kraja", "Eri Pasha", "Alba Gjini"],
  },
  {
    name: "Coast Club", host: "coastclub", color: "#0284C7", dark: false, location: "Vlorë", Logo: Waves,
    stats: { total: 1105, totalChange: 9, active: 742, activeChange: 6, revenue: 2968000, revenueChange: 12, inGym: 38 },
    hours: [8, 15, 23, 29, 26, 18, 14, 17, 21, 19, 22, 30, 36, 31, 22, 13, 5],
    members: ["Rei Ndreu", "Xhesi Bego", "Olsi Kuqi", "Fiona Dervishi", "Tea Mema", "Luan Bici", "Nora Hysa"],
  },
]

// ─── Admin labels (same strings as the admin's own translations) ─────────────

type AdminLang = "en" | "sq"

const ADMIN_COPY: Record<AdminLang, {
  sections: { members: string; operations: string; catalog: string; insights: string; account: string }
  nav: Record<"dashboard" | "users" | "plans" | "payments" | "checkIn" | "gyms" | "staff" | "classes" | "manageClasses" | "bookings" | "exercisePlans" | "diet" | "dietPlans" | "foodLibrary" | "dietCalendar" | "reviews" | "promotions" | "store" | "analytics" | "chat" | "billing", string>
  search: string
  title: string
  overviewOf: (gym: string) => string
  totalUsers: string; activeUsers: string; totalRevenue: string; activeCheckIns: string; currentlyInGym: string; vsLastMonth: string
  chartTitle: string; chartDesc: string; checkIns: string
  attendance: string; viewAll: string; active: string; checkOut: string
}> = {
  en: {
    sections: { members: "Members", operations: "Operations", catalog: "Catalog", insights: "Insights", account: "Account" },
    nav: {
      dashboard: "Dashboard", users: "Users", plans: "Plans", payments: "Payments", checkIn: "Check-in/out", gyms: "Gyms", staff: "Staff",
      classes: "Classes", manageClasses: "Manage Classes", bookings: "Bookings", exercisePlans: "Exercise Plans",
      diet: "Diet Management", dietPlans: "Diet Plans", foodLibrary: "Food Library", dietCalendar: "Diet Calendar",
      reviews: "Reviews", promotions: "Promotions", store: "Store", analytics: "Analytics", chat: "Chat Support", billing: "Billing",
    },
    search: "Search users...",
    title: "Dashboard",
    overviewOf: gym => `Overview of ${gym}`,
    totalUsers: "Total Users", activeUsers: "Active Users", totalRevenue: "Total Revenue", activeCheckIns: "Active Check-ins",
    currentlyInGym: "Currently in gym", vsLastMonth: "vs last month",
    chartTitle: "Check-ins by hour", chartDesc: "Distribution of member check-ins across the day", checkIns: "Check-ins",
    attendance: "Current Check-ins", viewAll: "View All", active: "Active", checkOut: "Check-out",
  },
  sq: {
    sections: { members: "Anëtarët", operations: "Operacionet", catalog: "Katalogu", insights: "Analiza", account: "Llogaria" },
    nav: {
      dashboard: "Paneli", users: "Përdoruesit", plans: "Planet", payments: "Pagesat", checkIn: "Hyrje/Dalje", gyms: "Palestrat", staff: "Stafi",
      classes: "Klasat", manageClasses: "Menaxho Klasat", bookings: "Rezervimet", exercisePlans: "Planet e Stërvitjes",
      diet: "Menaxhimi i Dietës", dietPlans: "Planet e Dietës", foodLibrary: "Biblioteka e Ushqimeve", dietCalendar: "Kalendari i Dietës",
      reviews: "Vlerësimet", promotions: "Promocionet", store: "Dyqani", analytics: "Analitika", chat: "Mbështetje Chat", billing: "Faturimi",
    },
    search: "Kërko përdorues...",
    title: "Paneli",
    overviewOf: gym => `Përmbledhje e ${gym}`,
    totalUsers: "Totali i Përdoruesve", activeUsers: "Përdoruesit Aktivë", totalRevenue: "Të Ardhurat Totale", activeCheckIns: "Hyrje Aktive",
    currentlyInGym: "Aktualisht në palestër", vsLastMonth: "nga muaji i kaluar",
    chartTitle: "Hyrjet sipas orës", chartDesc: "Shpërndarja e hyrjeve të anëtarëve gjatë ditës", checkIns: "Hyrje",
    attendance: "Hyrjet Aktuale", viewAll: "Shiko të gjitha", active: "Aktiv", checkOut: "Dil",
  },
}

// ─── Theme ───────────────────────────────────────────────────────────────────

/** Admin colour tokens as CSS variables; `--t` is the gym's brand colour (the admin's --primary). */
function themeVars(tenant: DemoTenant): CSSProperties {
  const dark = tenant.dark
  return {
    "--t": tenant.color,
    "--bg": dark ? "#0a0a0d" : "#fbfbfd",
    "--card": dark ? "#121216" : "#ffffff",
    "--fg": dark ? "#f2f2f3" : "#131318",
    "--muted": dark ? "#1f1f25" : "#f3f3f6",
    "--mfg": dark ? "#8e8e99" : "#6b6b76",
    "--line": dark ? "#26262d" : "#e8e8ed",
  } as CSSProperties
}

const tint = (pct: number) => `color-mix(in srgb, var(--t) ${pct}%, transparent)`

function formatAll(amount: number) {
  return `${amount.toLocaleString("en-US")} ALL`
}

function clock(minutes: number) {
  return `${String(Math.floor(minutes / 60) % 24).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

function TenantLogo({ tenant }: { tenant: DemoTenant }) {
  return (
    <span className="flex items-center gap-2.5">
      <span className="grid h-9 w-9 place-items-center rounded-xl text-white shadow-sm" style={{ backgroundColor: "var(--t)" }}>
        <tenant.Logo className="h-5 w-5" strokeWidth={2.25} />
      </span>
      <span className="text-[17px] font-bold tracking-tight">{tenant.name}</span>
    </span>
  )
}

function NavItem({ icon: Icon, label, active, chevron, child, badge }: { icon?: typeof Users; label: string; active?: boolean; chevron?: "down" | "right"; child?: boolean; badge?: string }) {
  return (
    <div
      className={`relative flex items-center rounded-md text-[13px] ${child ? "h-8 pl-10 pr-3" : "h-9 px-3"} ${
        active ? "bg-[var(--muted)] font-medium text-[color:var(--fg)]" : "text-[color:var(--mfg)]"
      }`}
    >
      {active && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full" style={{ backgroundColor: "var(--t)" }} />}
      {Icon && <Icon className="mr-2.5 h-4 w-4 shrink-0" />}
      <span className="flex-1 truncate">{label}</span>
      {badge && <span className="ml-auto rounded-full bg-[var(--muted)] px-1.5 text-xs font-semibold text-[color:var(--fg)]">{badge}</span>}
      {chevron === "down" && <ChevronDown className="ml-auto h-3.5 w-3.5 opacity-60" />}
      {chevron === "right" && <ChevronRight className="ml-auto h-3.5 w-3.5 opacity-60" />}
    </div>
  )
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <p className="px-3 pb-1 pt-4 text-[11px] font-medium uppercase tracking-wider text-[color:var(--mfg)] opacity-70">{children}</p>
}

function Sidebar({ tenant, copy }: { tenant: DemoTenant; copy: typeof ADMIN_COPY.en }) {
  const n = copy.nav
  return (
    <aside className="flex h-full w-[240px] shrink-0 flex-col border-r border-[color:var(--line)] bg-[var(--card)]">
      <div className="flex h-14 items-center px-4">
        <TenantLogo tenant={tenant} />
      </div>
      <nav className="grid gap-0.5 px-3 py-3">
        <NavItem icon={LayoutDashboard} label={n.dashboard} active />
        <SectionTitle>{copy.sections.members}</SectionTitle>
        <NavItem icon={Users} label={n.users} />
        <NavItem icon={CreditCard} label={n.plans} />
        <NavItem icon={CreditCard} label={n.payments} />
        <NavItem icon={QrCode} label={n.checkIn} />
        <SectionTitle>{copy.sections.operations}</SectionTitle>
        <NavItem icon={Building2} label={n.gyms} />
        <NavItem icon={UserCog} label={n.staff} />
        <NavItem icon={Calendar} label={n.classes} chevron="down" />
        <NavItem label={n.manageClasses} child />
        <NavItem label={n.bookings} child />
        <NavItem icon={Dumbbell} label={n.exercisePlans} />
        <NavItem icon={Utensils} label={n.diet} chevron="right" />
        <SectionTitle>{copy.sections.catalog}</SectionTitle>
        <NavItem icon={Star} label={n.reviews} />
        <NavItem icon={Tag} label={n.promotions} />
        <NavItem icon={ShoppingBag} label={n.store} />
        <SectionTitle>{copy.sections.insights}</SectionTitle>
        <NavItem icon={BarChart3} label={n.analytics} />
        <NavItem icon={MessageSquare} label={n.chat} badge="3" />
        <SectionTitle>{copy.sections.account}</SectionTitle>
        <NavItem icon={Receipt} label={n.billing} />
      </nav>
    </aside>
  )
}

function TopBar({ tenant, copy, compact }: { tenant: DemoTenant; copy: typeof ADMIN_COPY.en; compact: boolean }) {
  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-[color:var(--line)] bg-[var(--card)] px-6">
      {compact ? (
        <span className="grid h-10 w-10 place-items-center"><Menu className="h-5 w-5" /></span>
      ) : (
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--mfg)]" />
          <div className="flex h-10 items-center rounded-md border border-[color:var(--line)] bg-[var(--card)] pl-9 text-sm text-[color:var(--mfg)]">{copy.search}</div>
        </div>
      )}
      <div className="flex-1" />
      <div className="flex h-9 w-[180px] items-center gap-2 rounded-md border border-[color:var(--line)] px-3 text-sm">
        <Building2 className="h-4 w-4 shrink-0 text-[color:var(--mfg)]" />
        <span className="flex-1 truncate">{tenant.location}</span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </div>
      <span className="grid h-10 w-10 place-items-center text-[color:var(--mfg)]">
        {tenant.dark ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
      </span>
      <span className="grid h-10 w-10 place-items-center"><Volume2 className="h-5 w-5" /></span>
      <span className="grid h-8 w-8 place-items-center rounded-full border-2 p-0.5" style={{ borderColor: "var(--t)" }}>
        <span className="grid h-full w-full place-items-center rounded-full text-white" style={{ backgroundColor: "var(--t)" }}>
          <tenant.Logo className="h-3.5 w-3.5" strokeWidth={2.5} />
        </span>
      </span>
    </header>
  )
}

function Card({ className = "", children }: { className?: string; children: ReactNode }) {
  return <div className={`rounded-xl border border-[color:var(--line)] bg-[var(--card)] shadow-sm ${className}`}>{children}</div>
}

function TrendChip({ change }: { change: number }) {
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-xs font-medium text-emerald-600">
      <ArrowUpRight className="h-3 w-3" />+{change}%
    </span>
  )
}

function StatCard({ title, value, secondary, change, note, icon: Icon, copy }: { title: string; value: string; secondary?: string; change?: number; note?: string; icon: typeof UsersIcon; copy: typeof ADMIN_COPY.en }) {
  return (
    <Card>
      <div className="p-5">
        <div className="flex items-start justify-between">
          <p className="text-xs font-medium uppercase tracking-wide text-[color:var(--mfg)]">{title}</p>
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--muted)] text-[color:var(--mfg)]">
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-baseline gap-x-2">
          <span className="text-2xl font-semibold tracking-tight tabular-nums">{value}</span>
          {secondary && <span className="text-sm text-[color:var(--mfg)]">{secondary}</span>}
        </div>
        <div className="mt-2 flex items-center gap-2 text-xs text-[color:var(--mfg)]">
          {typeof change === "number" ? (
            <>
              <TrendChip change={change} />
              <span>{copy.vsLastMonth}</span>
            </>
          ) : (
            <span>{note}</span>
          )}
        </div>
      </div>
    </Card>
  )
}

/** Area chart drawn like the admin's recharts one; it re-draws left to right when the gym changes. */
function CheckInsChart({ hours, height, copy, drawKey }: { hours: number[]; height: number; copy: typeof ADMIN_COPY.en; drawKey: string }) {
  const W = 600
  const H = height
  const left = 36
  const bottom = 24
  const top = 8
  const max = Math.ceil(Math.max(...hours) / 10) * 10
  const x = (i: number) => left + (i / (hours.length - 1)) * (W - left - 12)
  const y = (v: number) => top + (1 - v / max) * (H - top - bottom)
  const points = hours.map((v, i) => [x(i), y(v)] as const)
  // Smooth (monotone-ish) path through the points.
  const line = points.reduce((d, [px, py], i) => {
    if (i === 0) return `M${px},${py}`
    const [qx, qy] = points[i - 1]
    const mid = (qx + px) / 2
    return `${d} C${mid},${qy} ${mid},${py} ${px},${py}`
  }, "")
  const area = `${line} L${x(hours.length - 1)},${H - bottom} L${left},${H - bottom} Z`
  const peak = hours.indexOf(Math.max(...hours))
  const [peakX, peakY] = points[peak]
  const ticks = [0, max / 4, max / 2, (3 * max) / 4, max]

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: H }} aria-hidden>
      <defs>
        <linearGradient id="tnf-admin-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="var(--t)" stopOpacity={0.3} />
          <stop offset="95%" stopColor="var(--t)" stopOpacity={0.03} />
        </linearGradient>
        <clipPath id={`tnf-admin-draw-${drawKey}`}>
          <rect x="0" y="0" width={W} height={H} className="tnf-chart-draw" />
        </clipPath>
      </defs>
      {ticks.map(v => (
        <g key={v}>
          <line x1={left} x2={W - 12} y1={y(v)} y2={y(v)} stroke="var(--line)" strokeDasharray="3 3" />
          <text x={left - 8} y={y(v) + 4} textAnchor="end" fontSize="12" fill="var(--mfg)">{v}</text>
        </g>
      ))}
      {hours.map((_, i) => i % 2 === 0 && (
        <text key={i} x={x(i)} y={H - 4} textAnchor="middle" fontSize="12" fill="var(--mfg)">{`${String(6 + i).padStart(2, "0")}:00`}</text>
      ))}
      <g clipPath={`url(#tnf-admin-draw-${drawKey})`}>
        <path d={area} fill="url(#tnf-admin-fill)" />
        <path d={line} fill="none" stroke="var(--t)" strokeWidth="2" />
        <circle cx={peakX} cy={peakY} r="4" fill="var(--card)" stroke="var(--t)" strokeWidth="2" />
      </g>
      {/* Hover tooltip, frozen on the busiest hour */}
      <g transform={`translate(${Math.min(peakX + 10, W - 130)}, ${Math.max(peakY - 52, top)})`} className="tnf-chart-tip">
        <rect width="118" height="46" rx="8" fill="var(--card)" stroke="var(--line)" />
        <text x="10" y="18" fontSize="11" fontWeight="600" fill="var(--fg)">{`${String(6 + peak).padStart(2, "0")}:00`}</text>
        <rect x="10" y="27" width="8" height="8" rx="2" fill="var(--t)" />
        <text x="24" y="35" fontSize="11" fill="var(--mfg)">{copy.checkIns}</text>
        <text x="108" y="35" fontSize="11" fontWeight="600" textAnchor="end" fill="var(--fg)">{hours[peak]}</text>
      </g>
    </svg>
  )
}

function CheckInRow({ name, time, copy, fresh }: { name: string; time: string; copy: typeof ADMIN_COPY.en; fresh: boolean }) {
  const initials = name.split(" ").map(part => part[0]).join("")
  return (
    <div className={`flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 ${fresh ? "tnf-feed-in" : ""}`}>
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-medium" style={{ backgroundColor: tint(12), color: "var(--t)" }}>
          {initials}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{name}</p>
          <p className="truncate text-xs text-[color:var(--mfg)]">10-10-2026 {time}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600">{copy.active}</span>
        <span className="flex h-9 items-center rounded-md border border-[color:var(--line)] px-3 text-sm font-medium">{copy.checkOut}</span>
      </div>
    </div>
  )
}

// ─── Screen ──────────────────────────────────────────────────────────────────

const SCREEN = { wide: { w: 1280, h: 800 }, compact: { w: 720, h: 900 } }

/** The admin dashboard for one gym, at real size (scaled by the caller). */
function AdminScreen({ tenant, lang, compact, tick }: { tenant: DemoTenant; lang: AdminLang; compact: boolean; tick: number }) {
  const copy = ADMIN_COPY[lang]
  const s = tenant.stats
  const startMinute = 8 * 60 + 12
  const checkIns = Array.from({ length: 5 }, (_, i) => {
    const n = tick + 4 - i
    return { id: n, name: tenant.members[n % tenant.members.length], time: clock(startMinute + n * 7) }
  })

  return (
    <div className="flex h-full bg-[var(--bg)] text-[color:var(--fg)]">
      {!compact && <Sidebar tenant={tenant} copy={copy} />}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar tenant={tenant} copy={copy} compact={compact} />
        <main className={`flex-1 overflow-hidden ${compact ? "p-6" : "p-8"}`}>
          <div className="space-y-8">
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold tracking-tight">{copy.title}</h1>
              <p className="text-sm text-[color:var(--mfg)]">{copy.overviewOf(`${tenant.name} · ${tenant.location}`)}</p>
            </div>

            <div className={`grid gap-4 ${compact ? "grid-cols-2" : "grid-cols-4"}`}>
              <StatCard title={copy.totalUsers} value={s.total.toLocaleString("en-US")} change={s.totalChange} icon={UsersIcon} copy={copy} />
              <StatCard title={copy.activeUsers} value={s.active.toLocaleString("en-US")} change={s.activeChange} icon={ActivityIcon} copy={copy} />
              <StatCard title={copy.totalRevenue} value={formatAll(s.revenue)} secondary={`€${(s.revenue / 100).toLocaleString("en-US")}`} change={s.revenueChange} icon={CreditCardIcon} copy={copy} />
              <StatCard title={copy.activeCheckIns} value={String(s.inGym + tick)} note={copy.currentlyInGym} icon={DumbbellIcon} copy={copy} />
            </div>

            <div className={`grid gap-6 ${compact ? "grid-cols-1" : "grid-cols-3"}`}>
              <Card className={compact ? "" : "col-span-2"}>
                <div className="space-y-1.5 p-6">
                  <div className="text-lg font-semibold leading-none tracking-tight">{copy.chartTitle}</div>
                  <div className="text-sm text-[color:var(--mfg)]">{copy.chartDesc}</div>
                </div>
                <div className="p-6 pt-0">
                  <CheckInsChart key={tenant.host} hours={tenant.hours} height={compact ? 240 : 270} copy={copy} drawKey={tenant.host} />
                </div>
              </Card>
              {!compact && (
                <Card>
                  <div className="flex items-center justify-between p-6">
                    <div className="text-lg font-semibold leading-none tracking-tight">{copy.attendance}</div>
                    <span className="flex h-9 items-center rounded-md border border-[color:var(--line)] px-3 text-sm font-medium">{copy.viewAll}</span>
                  </div>
                  <div className="divide-y divide-[color:var(--line)] p-6 pt-0">
                    {checkIns.map((row, i) => (
                      <CheckInRow key={row.id} name={row.name} time={row.time} copy={copy} fresh={i === 0 && tick > 0} />
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

/** Scales a fixed-size screen to the width of its container, like a screenshot. */
function ScaledScreen({ width, height, children }: { width: number; height: number; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => setScale(el.clientWidth / width)
    update()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [width])

  return (
    <div ref={ref} className="relative w-full overflow-hidden" style={{ aspectRatio: `${width} / ${height}` }}>
      <div className="absolute left-0 top-0 origin-top-left" style={{ width, height, transform: `scale(${scale})` }}>
        {children}
      </div>
    </div>
  )
}

function useIsCompact(query = "(max-width: 639px)") {
  const [compact, setCompact] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(query).matches)
  useEffect(() => {
    const media = window.matchMedia?.(query)
    if (!media) return
    const onChange = () => setCompact(media.matches)
    media.addEventListener?.("change", onChange)
    return () => media.removeEventListener?.("change", onChange)
  }, [query])
  return compact
}

/**
 * Hero visual: the admin in a browser window, cycling through demo gyms. Picking a gym from the
 * pills underneath stops the cycle; reduced-motion users get no cycle at all.
 */
export function AdminShowcase({ lang, tenantsLabel }: { lang: string; tenantsLabel: string }) {
  const reduced = usePrefersReducedMotion()
  const compact = useIsCompact()
  const [{ index, tick }, setState] = useState({ index: 0, tick: 0 })
  const [pinned, setPinned] = useState(false)
  const tenant = DEMO_TENANTS[index]
  const screen = compact ? SCREEN.compact : SCREEN.wide

  // New check-ins arrive every couple of seconds; after a few, the next gym takes over.
  useEffect(() => {
    if (reduced || pinned) return
    const timer = setInterval(() => {
      setState(({ index, tick }) =>
        tick >= 2 ? { index: (index + 1) % DEMO_TENANTS.length, tick: 0 } : { index, tick: tick + 1 }
      )
    }, 2200)
    return () => clearInterval(timer)
  }, [reduced, pinned])

  const pick = (i: number) => {
    setPinned(true)
    setState({ index: i, tick: 0 })
  }

  return (
    <div>
      <div className="relative">
        <div
          aria-hidden
          className="absolute -inset-x-8 -top-10 bottom-0 -z-10 rounded-[2.5rem] blur-3xl transition-colors duration-700"
          style={{ backgroundColor: `${tenant.color}38` }}
        />
        <BrowserChrome url={`${tenant.host}.thenotfit.com/dashboard`}>
          <div className="tnf-admin" style={themeVars(tenant)}>
            <ScaledScreen width={screen.w} height={screen.h}>
              <AdminScreen tenant={tenant} lang={lang === "sq" ? "sq" : "en"} compact={compact} tick={tick} />
            </ScaledScreen>
          </div>
        </BrowserChrome>

        <FloatingChip className="-left-8 top-[44%] hidden sm:block" delay="0.2s">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-lg transition-colors duration-700" style={{ backgroundColor: `${tenant.color}1F`, color: tenant.color }}>
              <ScanLine className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <div className="leading-tight">
              <div className="text-sm font-semibold">Checked in · QR</div>
              <div className="tnf-mono text-[11px] text-muted-foreground">{tenant.members[(tick + 4) % tenant.members.length]}</div>
            </div>
          </div>
        </FloatingChip>
        <FloatingChip className="-bottom-7 left-12 hidden sm:block" delay="1.6s">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-500/10 text-emerald-600">
              <Sparkles className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <div className="leading-tight">
              <div className="text-sm font-semibold">AI diet plan ready</div>
              <div className="tnf-mono text-[11px] text-muted-foreground">Weight loss · 1,650 kcal</div>
            </div>
          </div>
        </FloatingChip>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-2 lg:justify-start lg:pl-10">
        <span className="mr-1 w-full text-center text-xs font-medium text-muted-foreground sm:w-auto">{tenantsLabel}</span>
        {DEMO_TENANTS.map((t, i) => (
          <button
            key={t.host}
            type="button"
            onClick={() => pick(i)}
            aria-pressed={i === index}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-all ${
              i === index ? "bg-background shadow-md" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            style={i === index ? { borderColor: `${t.color}66` } : undefined}
          >
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color }} />
            {t.name}
          </button>
        ))}
      </div>
    </div>
  )
}
